import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Sheet, SheetContent } from '../components/ui/sheet';
import { toast } from 'sonner';
import {
  FileText, Plus, FileArchive, Image, ArrowRight,
  Clock, CheckCircle, XCircle, Loader2, AlertCircle,
  Coins, History, ShieldCheck, Menu,
} from 'lucide-react';
import { buildReuseGenerateUrl } from '../lib/documentReuseRoute';
import {
  DashboardSidebarDesktop,
  DashboardSidebarMobileContent,
} from '../components/dashboard/DashboardSidebar';
import { DashboardFooter } from '../components/dashboard/DashboardFooter';
import { QuickActionCard } from '../components/dashboard/QuickActionCard';
import {
  QUICK_ACTION_TIER1_ROW1,
  QUICK_ACTION_TIER1_ROW2_CENTER,
  QUICK_ACTION_TIER2,
  DOCUMENT_LIBRARY_ITEMS,
  resolveCreditCost,
} from '../lib/dashboardNavConfig';

export default function DashboardPage() {
  const { user, api, logout } = useAuth();
  const navigate = useNavigate();
  const [balance, setBalance] = useState(0);
  const [jobs, setJobs] = useState([]);
  const [totalJobs, setTotalJobs] = useState(0);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastDoc, setLastDoc] = useState(null);
  const [creditMap, setCreditMap] = useState({});
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [balanceRes, jobsRes, ledgerRes, lastRes, typesRes, servicesRes] = await Promise.all([
        api.get('/credits/balance'),
        api.get('/jobs/list?limit=5'),
        api.get('/credits/ledger?limit=5'),
        api.get('/documents/last').catch(() => ({ data: { has_last: false } })),
        api.get('/documents/types'),
        api.get('/service-credits'),
      ]);
      setBalance(balanceRes.data.balance);
      setJobs(Array.isArray(jobsRes.data) ? jobsRes.data : (jobsRes.data.jobs || []));
      setTotalJobs(jobsRes.data.total ?? (Array.isArray(jobsRes.data) ? jobsRes.data.length : (jobsRes.data.jobs || []).length));
      setLedger(Array.isArray(ledgerRes.data) ? ledgerRes.data : (ledgerRes.data.entries || []));
      setLastDoc(lastRes.data?.has_last ? lastRes.data : null);

      const map = { ...(servicesRes.data?.services || {}) };
      (typesRes.data?.types || []).forEach((t) => {
        map[t.id] = t.credits;
      });
      setCreditMap(map);
    } catch {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const guardServiceAccess = useCallback((creditsNeeded) => {
    if (!user?.email_verified && user?.role === 'user') {
      toast.error('Please verify your email to use this service.');
      return false;
    }
    if (balance < creditsNeeded) {
      toast.error('Insufficient credits. Please add credits to continue.');
      navigate('/credits');
      return false;
    }
    return true;
  }, [user, balance, navigate]);

  const handleQuickOrNavClick = (route, creditKey) => {
    const cost = resolveCreditCost(creditMap, creditKey);
    if (!guardServiceAccess(cost)) return;
    navigate(route);
  };

  const handleLibraryClick = (item) => {
    if (item.useBuilder || item.id === 'business_proposal') {
      navigate('/services/business-proposal-builder');
      return;
    }
    navigate(`/documents/${item.slug}/templates`);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-4 w-4 text-primary" />;
      case 'failed': return <XCircle className="h-4 w-4 text-destructive" />;
      case 'processing': return <Loader2 className="h-4 w-4 text-primary animate-spin" />;
      default: return <Clock className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getServiceIcon = (type) => {
    switch (type) {
      case 'document_generation': return <FileText className="h-4 w-4" />;
      case 'pdf_compression': return <FileArchive className="h-4 w-4" />;
      case 'visa_photo': return <Image className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const isVerified = user?.email_verified;

  return (
    <div className="min-h-screen bg-muted/20">
      <DashboardSidebarDesktop user={user} onLogout={handleLogout} />

      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent side="left" className="w-[min(280px,85vw)] p-0">
          <DashboardSidebarMobileContent
            user={user}
            onLogout={handleLogout}
            onClose={() => setMobileNavOpen(false)}
          />
        </SheetContent>
      </Sheet>

      {/* Mobile top bar */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-30 border-b border-border bg-card safe-area-top">
        <div className="flex h-14 items-center justify-between gap-2 px-3">
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0"
            onClick={() => setMobileNavOpen(true)}
            data-testid="mobile-nav-toggle"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2 min-w-0">
            {isVerified ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 dark:bg-emerald-950/50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-200/80 dark:ring-emerald-800">
                <ShieldCheck className="h-3 w-3" />
                Verified
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 dark:bg-amber-950/50 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-300 ring-1 ring-amber-200/80 dark:ring-amber-800">
                Pending
              </span>
            )}
            <span className="inline-flex items-center gap-1 rounded-full bg-orange-50 dark:bg-orange-950/50 px-2 py-0.5 text-[11px] font-medium text-orange-700 dark:text-orange-300 ring-1 ring-orange-200/80 dark:ring-orange-800">
              <Coins className="h-3 w-3" />
              {balance}
            </span>
          </div>
        </div>
      </header>

      <main className="md:ml-[220px] pt-14 md:pt-0 p-4 md:p-8 lg:p-10">
        <div className="max-w-6xl mx-auto space-y-8">
          {/* Header */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-1">
              <h1 className="text-2xl font-bold text-foreground">
                Welcome back, {user?.full_name?.split(' ')[0]}
              </h1>
              <p className="text-muted-foreground text-sm sm:text-base">
                Create business plans, grant proposals, service proposals, purchase orders, resumes, invoices, quotes, waybill, compress PDFs &amp; generate visa photos fast!
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:justify-end">
              {isVerified ? (
                <span
                  className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 dark:bg-emerald-950/50 px-3 py-1 text-xs font-medium text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-200/80 dark:ring-emerald-800"
                  data-testid="header-verified-pill"
                >
                  <ShieldCheck className="h-3.5 w-3.5" />
                  Verified
                </span>
              ) : (
                <span
                  className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 dark:bg-amber-950/50 px-3 py-1 text-xs font-medium text-amber-700 dark:text-amber-300 ring-1 ring-amber-200/80 dark:ring-amber-800"
                  data-testid="header-verified-pill"
                >
                  Pending Verification
                </span>
              )}
              <span
                className="inline-flex items-center gap-1.5 rounded-full bg-orange-50 dark:bg-orange-950/50 px-3 py-1 text-xs font-medium text-orange-700 dark:text-orange-300 ring-1 ring-orange-200/80 dark:ring-orange-800"
                data-testid="header-credits-pill"
              >
                <Coins className="h-3.5 w-3.5" />
                {balance} credits
              </span>
              <Link to="/credits" className="hidden sm:block">
                <Button data-testid="buy-credits-btn" size="sm" className="glow-button font-medium">
                  <Plus className="mr-1.5 h-4 w-4" />
                  Add Credits
                </Button>
              </Link>
            </div>
          </div>

          {balance > 0 && balance <= 3 && (
            <div
              data-testid="credit-low-nudge"
              className="rounded-2xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-4 sm:p-5 flex items-start gap-3"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-card shadow-sm ring-1 ring-amber-100 dark:ring-amber-900">
                <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-foreground">
                  You have enough credits for 1 more document — generate it now
                </p>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {balance} {balance === 1 ? 'credit' : 'credits'} left. Add more to keep building documents without interruption.
                </p>
              </div>
              <Link to="/credits">
                <Button size="sm" variant="outline" data-testid="credit-low-add-btn">
                  Add Credits
                </Button>
              </Link>
            </div>
          )}

          {lastDoc && lastDoc.has_last && (
            <Card className="card-elevated bg-primary/5 border-primary/20" data-testid="reuse-last-document">
              <CardContent className="py-5 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex items-center gap-3 flex-1">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-card shadow-sm ring-1 ring-primary/20">
                    <History className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold text-foreground">Reuse last document</div>
                    <div className="text-xs text-muted-foreground">
                      Last created: {(lastDoc.document_type || 'document').replace(/_/g, ' ')} — pre-fills your details so you can edit and regenerate.
                    </div>
                  </div>
                </div>
                <Button
                  data-testid="reuse-last-btn"
                  onClick={() => navigate(buildReuseGenerateUrl(lastDoc.document_type, lastDoc.job_id, {
                    templateId: lastDoc.template_id || undefined,
                  }))}
                  className="shrink-0"
                >
                  Reuse Now
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Quick Actions — dominant visual hierarchy */}
          <section data-testid="dashboard-quick-actions" className="space-y-5">
            <div>
              <h2 className="text-2xl font-bold text-foreground">Quick Actions</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Start with our flagship proposal tools, or jump into everyday documents.
              </p>
            </div>

            {/* Tier 1 — flagship proposals */}
            <div className="space-y-4" data-testid="quick-actions-tier-1">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {QUICK_ACTION_TIER1_ROW1.map((item) => (
                  <QuickActionCard
                    key={item.id}
                    item={item}
                    credits={resolveCreditCost(creditMap, item.creditKey)}
                    premium
                    testId={`quick-action-${item.id}`}
                    onClick={() => handleQuickOrNavClick(item.route, item.creditKey)}
                  />
                ))}
              </div>
              {QUICK_ACTION_TIER1_ROW2_CENTER && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div className="hidden lg:block" aria-hidden="true" />
                  <QuickActionCard
                    key={QUICK_ACTION_TIER1_ROW2_CENTER.id}
                    item={QUICK_ACTION_TIER1_ROW2_CENTER}
                    credits={resolveCreditCost(creditMap, QUICK_ACTION_TIER1_ROW2_CENTER.creditKey)}
                    premium
                    testId={`quick-action-${QUICK_ACTION_TIER1_ROW2_CENTER.id}`}
                    onClick={() => handleQuickOrNavClick(
                      QUICK_ACTION_TIER1_ROW2_CENTER.route,
                      QUICK_ACTION_TIER1_ROW2_CENTER.creditKey,
                    )}
                  />
                  <div className="hidden lg:block" aria-hidden="true" />
                </div>
              )}
            </div>

            {/* Tier 2 — everyday tools (same card footprint as Tier 1) */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="quick-actions-tier-2">
              {QUICK_ACTION_TIER2.map((item) => (
                <QuickActionCard
                  key={item.id}
                  item={item}
                  credits={resolveCreditCost(creditMap, item.creditKey)}
                  premium={false}
                  testId={`quick-action-${item.id}`}
                  onClick={() => handleQuickOrNavClick(item.route, item.creditKey)}
                />
              ))}
            </div>
          </section>

          {balance === 0 && (
            <Card className="border-destructive/20 bg-destructive/5">
              <CardContent className="flex items-center gap-4 py-4">
                <AlertCircle className="h-5 w-5 text-destructive" />
                <div className="flex-1">
                  <p className="font-medium text-foreground">You need credits to continue</p>
                  <p className="text-sm text-muted-foreground">Add credits to start processing documents.</p>
                </div>
                <Link to="/credits">
                  <Button size="sm">Add Credits</Button>
                </Link>
              </CardContent>
            </Card>
          )}

          {/* Stats — credit balance + total jobs only */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card className="card-elevated bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20" data-testid="balance-card">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2 text-primary font-medium">
                  <Coins className="h-4 w-4" />
                  Credit balance
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-bold text-foreground">{balance}</div>
                <p className="text-sm text-muted-foreground mt-1">credits available</p>
              </CardContent>
            </Card>

            <Card className="card-elevated" data-testid="jobs-count-card">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2 font-medium">
                  <History className="h-4 w-4" />
                  Total jobs
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-bold text-foreground">{totalJobs}</div>
                <p className="text-sm text-muted-foreground mt-1">documents processed</p>
              </CardContent>
            </Card>
          </div>

          {/* Document Library */}
          <Card className="card-elevated" data-testid="document-library-card">
            <CardHeader>
              <CardTitle className="text-foreground">Document Library</CardTitle>
              <CardDescription>
                Choose a document below and generate it instantly. Credit cost is shown for each template.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {DOCUMENT_LIBRARY_ITEMS.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    data-testid={`doc-library-${item.id}`}
                    onClick={() => handleLibraryClick(item)}
                    className="flex items-center gap-3 rounded-full border border-[#E8E8E0] dark:border-[#3a3a36] bg-[#F5F5F0] dark:bg-[#2a2a26] px-4 py-3 text-left transition-colors cursor-pointer hover:bg-[#EDECE6] dark:hover:bg-[#33332f] hover:border-[#F47518]/70 dark:hover:border-[#F47518]/50"
                  >
                    <i
                      className={`ti ${item.icon} text-lg shrink-0 text-[#F47518]`}
                      aria-hidden
                    />
                    <span className="font-bold text-base text-foreground leading-tight">
                      {item.name}
                    </span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Card className="card-elevated" data-testid="recent-jobs-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-foreground">Recent Jobs</CardTitle>
                  <CardDescription>Your latest document processing</CardDescription>
                </div>
                <Link to="/jobs">
                  <Button variant="ghost" size="sm" className="text-primary font-medium">
                    View All
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </Link>
              </CardHeader>
              <CardContent>
                {jobs.length === 0 ? (
                  <div className="text-center py-10">
                    <History className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
                    <p className="text-sm text-muted-foreground">You haven&apos;t processed anything yet.</p>
                    <p className="text-sm text-muted-foreground">Start by generating a document or compressing a file.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {jobs.map((job) => (
                      <div key={job.id} className="flex items-center justify-between p-4 rounded-xl bg-muted/50">
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-lg bg-card flex items-center justify-center shadow-sm">
                            {getServiceIcon(job.service_type)}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground">{job.service_type.replace(/_/g, ' ')}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(job.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {getStatusIcon(job.status)}
                          <span className="text-xs text-muted-foreground">{job.status}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="card-elevated" data-testid="credit-history-card">
              <CardHeader>
                <CardTitle className="text-foreground">Credit History</CardTitle>
                <CardDescription>Recent transactions</CardDescription>
              </CardHeader>
              <CardContent>
                {ledger.length === 0 ? (
                  <div className="text-center py-10">
                    <Coins className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
                    <p className="text-sm text-muted-foreground">No transactions yet.</p>
                    <p className="text-sm text-muted-foreground">Add credits to get started.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {ledger.map((entry) => (
                      <div key={entry.id} className="flex items-center justify-between p-4 rounded-xl bg-muted/50">
                        <div>
                          <p className="text-sm font-medium text-foreground capitalize">{entry.transaction_type.replace(/_/g, ' ')}</p>
                          <p className="text-xs text-muted-foreground">{entry.description}</p>
                        </div>
                        <div className="text-right">
                          <p className={`text-sm font-semibold ${entry.amount >= 0 ? 'text-primary' : 'text-destructive'}`}>
                            {entry.amount >= 0 ? '+' : ''}{entry.amount}
                          </p>
                          <p className="text-xs text-muted-foreground">Bal: {entry.balance_after}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <DashboardFooter />
        </div>
      </main>
    </div>
  );
}
